import {
  Button, Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  makeStyles, MenuItem,
  Select,
  Slide,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import React, {useEffect, useRef, useState} from 'react';
import * as yup from 'yup';
import {
  Field,
  FieldArray, FieldArrayRenderProps,
  Formik,
  FormikErrors,
  FormikProps,
  FormikTouched,
  FormikValues,
  useFormik,
  useFormikContext,
  withFormik
} from 'formik';
import PredefinedRoute, {RouteEntry} from '../../../Domain/Models/PredefinedRoute';
import {predefinedRouteRepository} from "../../../Domain/Repositories/PredefinedRouteRepository";
import Loading from "../../UiComponents/Loading";
import Group from "../../../Domain/Models/Group";
import {cubeRepository} from "../../../Domain/Repositories/CubeRepository";
import {CheckBox} from "@material-ui/icons";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  form: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  dialog: {
    alignItems: 'flex-start',
  },
  deleteFlex: {
    flex: 1,
  }
}));

export interface PredefinedRouteModalProps
{
  open: boolean;
  onClose: () => void;
  group: Group;
  predefinedRoute?: PredefinedRoute;
}

export default function PredefinedRouteModal(props: PredefinedRouteModalProps)
{
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {open, onClose, group} = props;
  const classes = useStyles();

  const [predefinedRoute, setPredefinedRoute] = useState<PredefinedRoute>({
    id: '',
    group: group,
    anyOrder: false,
    route: []
  });
  const [updateMode, setUpdateMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const formRef = useRef<FormikProps<PredefinedRoute>>(null);

  const schema = yup.object().shape({
  });

  const handleSubmit = (value: PredefinedRoute) =>
  {
    setSubmitting(true);
    setSubmitError(undefined);
    setPredefinedRoute(value);

    if (updateMode)
    {
      predefinedRouteRepository.update(value)
        .then(() => onClose())
        .catch((e: Error) =>
        {
          setSubmitting(false);
          setSubmitError(e.message);
        });
    }
    else
    {
      predefinedRouteRepository.add(value)
        .then(() => onClose())
        .catch((e: Error) =>
        {
          setSubmitting(false);
          setSubmitError(e.message);
        });
    }
  }
  
  const handleDelete = () =>
  {
    setSubmitting(true);
    setSubmitError(undefined);
    
    predefinedRouteRepository.delete(predefinedRoute)
      .then(() => onClose())
      .catch((e: Error) =>
      {
        setSubmitting(false);
        setSubmitError(e.message);
      });
  }

  useEffect(() =>
  {
    if (open)
    {
      setSubmitError(undefined);
      setSubmitting(false);

      if (props.predefinedRoute !== undefined)
      {
        setPredefinedRoute(props.predefinedRoute);
        setUpdateMode(true);
      }
      else
      {
        setPredefinedRoute({
          id: '',
          group: group,
          anyOrder: false,
          route: []
        })
        setUpdateMode(false);
      }
    }
  }, [open]);

  return (
    <Dialog
      classes={{scrollPaper: classes.dialog}}
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      disableEscapeKeyDown
      disableBackdropClick={submitting}
      TransitionComponent={Transition}
    >
      <DialogTitle>PredefinedRoute</DialogTitle>

      <Loading isLoading={submitting}>
        <DialogContent>
          <Formik<PredefinedRoute>
            initialValues={predefinedRoute}
            onSubmit={(value) => handleSubmit(value)}
            validationSchema={schema}
            innerRef={formRef}
          >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur
              }) => (
              <>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Any Order</InputLabel>
                  <Checkbox
                    name="anyOrder"
                    checked={values.anyOrder}
                    onChange={handleChange}
                    />
                </FormControl>

                <FieldArray
                  name="route"
                  render={(arrayHelpers) => (
                    <>
                      {values.route
                        .sort((a, b) => a.index < b.index ? -1 : 1)
                        .map((x, index) =>
                      (
                        <FormControl fullWidth margin="normal">
                          <InputLabel>{`Cube ${index + 1}`}</InputLabel>
                          <Select
                            name={`route[${index}].id`}
                            value={x.cube.id}
                            onChange={event =>
                            {
                              const cube = cubeRepository.getById(event.target.value as string);
                              if (cube !== undefined)
                              {
                                arrayHelpers.replace(index, { index: index, cube: cube });
                              }
                            }}
                          >
                            {cubeRepository.cubes.map((value) => <MenuItem value={value.id}>{value.name}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ))}

                      <Button onClick={() => arrayHelpers.push({ index: values.route.length, cube: cubeRepository.cubes[values.route.length]})} color="primary">
                        Add Cube
                      </Button>
                      <Button
                        onClick={() => arrayHelpers.remove(values.route.length - 1)}
                        color="secondary"
                        disabled={values.route.length == 0}
                      >
                        Remove Last
                      </Button>
                    </>
                  )}
                />
              </>
            )}
          </Formik>

          {submitError != undefined && (
            <Typography component="h3" variant="subtitle1" color="error" gutterBottom>
              {submitError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          {updateMode && (
            <Button
              onClick={handleDelete} color="secondary"
            >
              Delete
            </Button>
          )}
          <Button
            onClick={onClose} color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => formRef.current?.handleSubmit()}
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>

      </Loading>
    </Dialog>
  );
}